import { type SEOHandle } from '@nasa-gcn/remix-seo'
import {
	Lock,
	Shield,
	ServerOff,
	Link as LinkIcon,
	Clock,
	Users,
	Zap,
	Gift,
	Rocket,
} from 'lucide-react'
import mainBlockerGif from '#app/assets/gif/main-blocker.gif'
import explainerPoster from '#app/assets/video/explainer-poster.jpg'
import explainerVideo from '#app/assets/video/explainer.mp4'
import { BlockRotatingText } from '#app/components/block-rotating-text.tsx'
import { Footer } from '#app/components/footer.tsx'
import { Header } from '#app/components/header.tsx'
import { LazyVideo } from '#app/components/lazy-video.tsx'
import { MainCTAButtons } from '#app/components/main-cta-buttons.tsx'
import { Pricing } from '#app/components/pricing.tsx'

import { Badge } from '#app/components/ui/badge'
import { Card, CardContent } from '#app/components/ui/card'

export function meta() {
	return [
		{ title: 'Task Sentry — Free AI Website Blocker' },
		{
			name: 'description',
			content:
				'Free AI website blocker for Chrome that stops distractions in real-time. 100% private on-device processing. Block the right sites at the right time.',
		},
		{
			name: 'keywords',
			content:
				'free ai website blocker, ai website blocker, free website blocker, chrome website blocker, distraction blocker, productivity extension',
		},
		{
			property: 'og:title',
			content: 'Task Sentry — Free AI Website Blocker',
		} as any,
		{
			property: 'og:description',
			content:
				'Free AI website blocker for Chrome that blocks distractions in real-time with private on-device AI.',
		} as any,
		{ property: 'og:type', content: 'website' } as any,
		{ property: 'og:url', content: 'https://tasksentry.app/' } as any,
		{
			property: 'og:image',
			content: 'https://tasksentry.app/assets/video/explainer-poster.jpg',
		} as any,
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: 'Task Sentry — Free AI Website Blocker' },
		{
			name: 'twitter:description',
			content:
				'Free AI website blocker for Chrome that blocks distractions in real-time with private on-device AI.',
		},
		{
			name: 'twitter:image',
			content: 'https://tasksentry.app/assets/video/explainer-poster.jpg',
		},
		{
			'script:ld+json': [
				{
					'@context': 'https://schema.org',
					'@type': 'VideoObject',
					name: 'Task Sentry - How It Works',
					description:
						'Watch how Task Sentry transforms your productivity with AI-powered distraction blocking while keeping everything private and local on your device.',
					thumbnailUrl:
						'https://tasksentry.app/assets/video/explainer-poster.jpg',
					uploadDate: '2025-10-29T00:00:00Z',
					duration: 'PT3M32S',
					contentUrl: 'https://tasksentry.app/assets/video/explainer.mp4',
				},
				{
					'@context': 'https://schema.org',
					'@type': 'SoftwareApplication',
					name: 'Task Sentry',
					description:
						'Free AI website blocker for Chrome that stops distractions in real-time. 100% private, on-device AI.',
					applicationCategory: 'ProductivityApplication',
					operatingSystem: 'Chrome',
					offers: {
						'@type': 'Offer',
						price: '0',
						priceCurrency: 'USD',
						category: 'free',
					},
					url: 'https://tasksentry.app/',
				},
			],
		},
	]
}

export const handle: SEOHandle = {
	getSitemapEntries: () => [
		{
			route: '/',
			priority: 1,
			changeFrequency: 'weekly',
		},
	],
}

const steps = [
	{
		id: 1,
		title: 'AI Blocks Distractions in Real-Time',
		description:
			'Task Sentry monitors your browsing and blocks distractions before they break your flow.',
		placeholder: 'AI Blocks Distractions',
		gradient: 'from-primary to-purple-400',
		img: mainBlockerGif,
		imgAlt: 'Task Sentry blocking distraction demonstration',
	},
]

const benefits = [
	{
		icon: Zap,
		title: 'AI-Powered & Context-Aware',
		description:
			"Unlike rigid blocklists, our AI understand you. It knows the difference between watching a kitten video on youtube and researching for work, so you're only blocked when you're truly procrastinating.",
		highlight: '🎯 Context matters',
	},
	{
		icon: Shield,
		title: 'Completely Private',
		description:
			'Our AI analyses your browsing data directly on your PC, ensuring your data never leaves your computer and is never uploaded anywhere. What you do is your business, period.',
		highlight: '🔒 Your data stays yours',
		isPrimary: true,
	},
	{
		icon: Clock,
		title: 'Effortless & Proactive',
		description:
			'Task Sentry works at the precise moment your willpower fails. It catches impulsive clicks in real-time and protects your focus even on low-motivation days.',
		highlight: '⚡ Always on guard',
	},
	{
		icon: Users,
		title: 'Your Executive Function Co-pilot',
		description:
			'Built for the brilliant but distractible mind. It provides the guardrails to stay on track, acting as your external willpower without the friction of a complicated system.',
		highlight: '🧠 Just works',
	},
]

export default function Home() {
	return (
		<div className="min-h-screen bg-white">
			<Header />
			{/* <Hero /> */}
			<section className="bg-gray-50 py-16 text-center sm:py-24">
				<div className="mx-auto max-w-5xl px-6">
					<div className="mb-8 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600">
						<Zap className="h-4 w-4 text-yellow-400" />
						<span>Free • AI-Powered • Private</span>
					</div>

					<BlockRotatingText
						domains={[
							'YouTube',
							'Reddit',
							'Twitter',
							'Facebook',
							'LinkedIn',
							'Internet',
						]}
						duration={2500}
						className="text-3xl leading-tight font-extrabold sm:text-6xl"
					/>

					<p className="text-muted-foreground mx-auto mt-16 max-w-3xl text-xl leading-relaxed italic sm:text-2xl">
						Task Sentry protects your focus <br />
						by blocking the right things at the right time.
					</p>

					<div className="mt-16 flex flex-col items-center gap-8">
						<MainCTAButtons />
					</div>
				</div>
			</section>
			{/* Privacy Trust Bar*/}
			<section className="border-b border-gray-100 bg-white py-6 sm:py-8">
				<div className="mx-auto max-w-4xl px-6">
					<div className="text-muted-foreground grid grid-cols-2 items-center justify-start gap-4 text-xs sm:flex sm:justify-center sm:gap-8 sm:text-sm">
						<div className="flex items-center justify-start gap-2">
							<Shield className="h-5 w-5 text-green-500" />
							<span className="font-medium">100% Local Processing</span>
						</div>
						<div className="flex items-center justify-start gap-2">
							<ServerOff className="h-5 w-5 text-green-500" />
							<span className="font-medium">No Browser Data Transfer</span>
						</div>
						<div className="flex items-center justify-start gap-2">
							<Zap className="h-5 w-5 text-green-500" />
							<span className="font-medium">Forever Free Plan</span>
						</div>
					</div>
				</div>
			</section>
			{/* Pain Points */}
			<section className="bg-gray-900 py-12 sm:py-20">
				<div className="mx-auto max-w-5xl px-6">
					<div className="mb-16 text-center">
						<h2 className="mb-6 text-2xl font-bold text-white sm:text-4xl">
							Does This Sound Familiar?
						</h2>
					</div>
					<div className="grid gap-8 md:grid-cols-3">
						<div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-800 to-gray-700 p-6 text-center transition-all duration-300 hover:scale-105 hover:shadow-2xl">
							<div className="from-primary/10 absolute inset-0 bg-gradient-to-br to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
							<div className="relative flex min-h-[120px] flex-col items-center justify-center gap-4">
								<Clock className="text-primary h-8 w-8" />
								<p className="text-xl leading-relaxed font-medium text-gray-100">
									That "quick check" that steals an hour.
								</p>
							</div>
						</div>
						<div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-800 to-gray-700 p-6 text-center transition-all duration-300 hover:scale-105 hover:shadow-2xl">
							<div className="from-primary/10 absolute inset-0 bg-gradient-to-br to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
							<div className="relative flex min-h-[120px] flex-col items-center justify-center gap-4">
								<LinkIcon className="text-primary h-8 w-8" />
								<p className="text-xl leading-relaxed font-medium text-gray-100">
									That one link that kills your afternoon.
								</p>
							</div>
						</div>
						<div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-800 to-gray-700 p-6 text-center transition-all duration-300 hover:scale-105 hover:shadow-2xl">
							<div className="from-primary/10 absolute inset-0 bg-gradient-to-br to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
							<div className="relative flex min-h-[120px] flex-col items-center justify-center gap-4">
								<Zap className="text-primary h-8 w-8" />
								<p className="text-xl leading-relaxed font-medium text-gray-100">
									That busy day that feels unproductive.
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* How It Works */}
			<section className="bg-white py-12 sm:py-20">
				<div className="mx-auto max-w-6xl px-6">
					<div className="mb-16 text-center">
						<h2 className="mb-4 text-2xl font-bold sm:text-4xl">
							Blocks the distraction, not your task.
						</h2>
						<p className="text-muted-foreground text-lg sm:text-xl">
							Watch Task Sentry protect your focus in real-time
						</p>
					</div>

					<div className="mx-auto max-w-3xl">
						{steps.map((step) => (
							<div key={step.id} className="text-center">
								<Card className="overflow-hidden bg-gray-50 p-0">
									<CardContent className="p-0">
										<img
											src={step.img}
											alt={step.imgAlt}
											className="w-full rounded-xl"
											loading="lazy"
										/>
									</CardContent>
								</Card>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Benefits */}
			<section className="bg-white py-12 sm:py-20">
				<div className="mx-auto max-w-4xl px-6">
					<div className="mb-16 text-center">
						<h2 className="mb-4 text-2xl font-bold sm:text-4xl">
							Why This Actually Works
						</h2>
						<p className="text-muted-foreground mx-auto max-w-2xl text-lg sm:text-xl">
							Most focus tools just block websites. Task Sentry understands what
							you're doing and blocks the right things at the right time.
						</p>
					</div>

					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
						{benefits.map((benefit, index) => (
							<Card
								key={index}
								className={`group p-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl ${
									benefit.isPrimary
										? 'bg-primary text-primary-foreground hover:shadow-primary/30'
										: 'bg-gray-50 hover:bg-white'
								}`}
							>
								<CardContent className="p-0">
									<div
										className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl ${
											benefit.isPrimary
												? 'bg-white/20'
												: 'bg-primary/10 text-primary'
										}`}
									>
										<benefit.icon className="h-8 w-8" />
									</div>
									<h3 className="mb-4 text-lg font-semibold sm:text-xl">
										{benefit.title}
									</h3>
									<p
										className={`mb-4 leading-relaxed ${
											benefit.isPrimary
												? 'text-primary-foreground/90'
												: 'text-muted-foreground'
										}`}
									>
										{benefit.description}
									</p>
									<Badge
										variant={benefit.isPrimary ? 'secondary' : 'outline'}
										className={
											benefit.isPrimary
												? 'text-primary-foreground bg-white/20 hover:bg-white/30'
												: ''
										}
									>
										{benefit.highlight}
									</Badge>
								</CardContent>
							</Card>
						))}
					</div>
				</div>
			</section>
			{/* Video Demo */}
			<section className="bg-gray-50 py-12 sm:py-20">
				<div className="mx-auto max-w-4xl px-6 text-center">
					<h2 className="mb-6 text-2xl font-bold sm:text-4xl">
						See how it works
					</h2>
					<p className="text-muted-foreground mb-12 text-lg sm:text-xl">
						Watch how Task Sentry transforms your productivity while keeping
						everything private
					</p>

					<div className="relative [aspect-ratio:1062/720] overflow-hidden rounded-2xl bg-gray-900 shadow-2xl">
						<LazyVideo
							src={explainerVideo}
							poster={explainerPoster}
							className="h-full w-full object-cover"
						>
							<track kind="captions" />
						</LazyVideo>
					</div>
				</div>
			</section>

			<section className="bg-white py-12 sm:py-20">
				<div className="mb-16 text-center">
					<h2 className="mb-6 text-2xl font-bold sm:text-4xl">
						Simple, Transparent Pricing
					</h2>
					<p className="text-muted-foreground text-lg sm:text-xl">
						Get started for free.
					</p>
				</div>
				<Pricing />
			</section>
			{/* Final CTA */}
			<section className="bg-primary text-primary-foreground py-12 text-center sm:py-20">
				<div className="mx-auto max-w-3xl px-6">
					<h2 className="mb-6 text-2xl font-bold sm:text-4xl">
						Ready to Focus Better?
					</h2>
					<p className="mb-10 text-lg opacity-90 sm:text-xl">
						Join the pioneers who've already upgraded their focus.
					</p>

					<div className="mb-8 flex flex-col justify-center gap-4 sm:flex-row">
						<MainCTAButtons variant="secondary" />
					</div>

					<div className="flex items-center justify-center gap-4 text-xs opacity-80 sm:gap-6 sm:text-sm">
						<div className="flex items-center gap-2">
							<Lock className="h-4 w-4" />
							<span>Private</span>
						</div>
						<div className="flex items-center gap-2">
							<Gift className="h-4 w-4" />
							<span>Free Plan</span>
						</div>
						<div className="flex items-center gap-2">
							<Rocket className="h-4 w-4" />
							<span>Local AI</span>
						</div>
					</div>
				</div>
			</section>
			<Footer />
		</div>
	)
}
